<script setup>
import { computed } from 'vue'
import { t, g1, i18n } from '@/locales/i18n'
import { usePromotions } from '@/composables/usePromotions'
import { useTier } from '@/composables/useTier'
import { useStore } from 'vuex'

import GlossaryWidget from '@/components/Dashboard/GlossaryWidget.vue'
import DDose from '@/components/Dashboard/DDose.vue'
import FutureCoaching from '@/components/Messages/FutureCoaching.vue'
import WeChatSnackbar from '@/components/Messages/WeChatSnackbar.vue'
import DashboardBanner from '@/components/Messages/DashboardBanner.vue'
import GTOReportsFlopDashboardBanner from '@/components/Banners/GTOReportsFlopDashboardBanner.vue'
import RbwizardPromoDashboardBanner from '@/components/Banners/RbwizardPromoDashboardBanner.vue'
import DashboardLinks from '@/components/Dashboard/DashboardLinks.vue'
import DashboardStats from '@/components/Dashboard/DashboardStats.vue'
import DashboardArticles from '@/components/Dashboard/DashboardArticles.vue'
import DashboardSubLinks from '@/components/Dashboard/DashboardSubLinks.vue'
import DashboardSocials from '@/components/Dashboard/DashboardSocials.vue'
import DashboardCoachings from '@/components/Dashboard/DashboardCoachings.vue'
import PlaymentSlotWrapper from '@/components/Payments/PlaymentSlotWrapper.vue'
import PokerArenaDashboardBanner from '@/components/Banners/PokerArenaDashboardBanner.vue'

const { userHasRbwizardPromo, isLoadingPromotions } = usePromotions()
const { gameformatTrial, nadaPurchased, affiliateName } = useTier('')
const store = useStore()

const mobile = computed(() => store.getters.globals_mobile)
</script>

<template>
  <div
    class="gto-dashboard gwizdash medium-scroll-track"
    style="min-height: 0"
  >
    <div
      class="gwizdash_content_wrapper"
      style="z-index: 4"
    >
      <div class="gwizdash_content">
        <div v-if="!g1">
          <RbwizardPromoDashboardBanner v-if="userHasRbwizardPromo" />
          <PokerArenaDashboardBanner
            v-else-if="!isLoadingPromotions && store.getters.user_has_flag('BANNER_POKER_ARENA')"
          />
          <GTOReportsFlopDashboardBanner
            v-else-if="!isLoadingPromotions && store.getters.user_has_flag('BANNER_GTO_REP_FLOP')"
          />
          <DashboardBanner
            v-else-if="!isLoadingPromotions && store.getters.user_has_flag('BANNER_EVENTS')"
          />
        </div>

        <PlaymentSlotWrapper v-if="gameformatTrial && nadaPurchased">
          <template #default="{ anyCheapPlan }">
            <div
              v-if="anyCheapPlan"
              class="f-center text-center pa-2 weight_500 b-radius_4"
              style="
                background-color: rgba(0, 0, 0, 0.3);
                color: var(--clr-primary);
                border: 1px solid rgba(7, 110, 91, 1);
                padding: 0.5rem 1rem;
              "
            >
              <div
                v-html="
                  !anyCheapPlan.discount
                    ? t('messages.enjoy_free_version', { trial_hours: 24 })
                    : anyCheapPlan.discount && anyCheapPlan.price_type === 'DISCOUNT'
                      ? t('messages.enjoy_free_version_discount', {
                          trial_hours: 24,
                          discount: anyCheapPlan.discount,
                        })
                      : t('messages.enjoy_free_version_affiliate', {
                          trial_hours: 24,
                          affiliate_name: affiliateName,
                          discount: anyCheapPlan.discount,
                        })
                "
              ></div>
              <div
                v-if="anyCheapPlan.discount"
                class="ml-4 gw_btn gw_btn_text"
                style="--clr-back: rgba(7, 110, 91, 0.125); color: var(--clr-white-0)"
                @click="
                  $router.push({
                    name: 'SubscriptionPaymentSelection',
                  })
                "
              >
                {{ t('btn.upgrade_now') }}
              </div>
            </div>
          </template>
        </PlaymentSlotWrapper>

        <WeChatSnackbar v-if="i18n.locale === 'zh'" />
        <FutureCoaching class="anim-fade-in" />
        <DashboardLinks class="anim-fade-in" />
        <DashboardStats
          class="anim-fade-in"
          style="--width-right: var(--width-right-column)"
        />
        <DDose
          v-if="!g1 && mobile"
          class="anim-fade-in"
        />
        <GlossaryWidget
          v-if="!g1 && mobile"
          class="anim-fade-in"
        />
        <DashboardCoachings
          v-if="!g1 && mobile"
          class="anim-fade-in"
        />

        <div
          v-if="!g1"
          class="gwizdash_content_block anim-fade-in"
          style="--gap: 1.5rem"
        >
          <DashboardArticles
            ref="articles"
            class="flex-1"
          />
          <div class="gwizdash_right">
            <DDose v-if="!mobile" />
            <GlossaryWidget v-if="!mobile" />
            <DashboardCoachings v-if="!mobile" />
            <DashboardSubLinks />
            <DashboardSocials />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.gwizdash {
  display: flex;
  min-height: var(--vh-3);
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  flex-direction: column;
  --width-right-column: 20rem;
  &_content_wrapper {
    min-height: var(--vh-3);
    flex-shrink: 0;
    position: relative;
  }
  &_content {
    margin: 0 auto;
    padding: 2.5rem 0.5rem;
    width: clamp(12rem, 75.5rem, 100vw);
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    &_block {
      --gap: 0rem;
      gap: var(--gap);
      width: 100%;
      display: flex;
    }
  }
  &_right {
    display: flex;
    flex-direction: column;
    --gap: 1rem;
    gap: var(--gap);
    max-width: var(--width-right-column);
    min-width: var(--width-right-column);
  }
}

@media (max-width: 1023px) {
  .gwizdash {
    --width-right-column: 100%;
    &_content {
      &_block {
        flex-direction: column;
      }
    }
    &_right {
      max-width: 100%;
    }
  }
}
</style>
